/**
 * Tipos para DERAG12_38R2Service — Puntos de encuentro ante maremotos
 *
 * Datos: DERA G12 servicios (capa única `g12_38_PuntosEncuentroMaremotos`)
 * Volumen: 715 features (verif. empírica 2026-05-18 sample n=300)
 * Cobertura: ~80 INEs litorales en las 5 provincias costeras de Andalucía
 * Contexto: Decreto 197/2024 · Plan Especial frente al Riesgo de Maremotos
 * Fuente: WFS IECA — https://www.ideandalucia.es/services/DERA_g12_servicios/wfs
 * CRS: EPSG:25830 (UTM30 ETRS89 — nativo, sin reproyección)
 *
 * Ruta R2: `dera/maremotos.json.gz` (global, DOP-RE-2=α)
 *   URL pública: `https://geodata.pteltool.es/v1/dera/maremotos`
 *
 * HX-CAPTURA-MAXIMAL (rumbo 2 · canonizado WAVE-1-MAREMOTOS-CAT 18-may):
 * el schema persistido preserva la totalidad de los campos fuente +
 * provenance + semánticos PTEL. No se reduce al denominador común con
 * otras capas DERA G12; el dataset es soberano de su propio schema.
 *
 * @module types/deraG12_38
 * @version 1.0.0
 * @date 2026-05-19
 */

// =============================================================================
// FEATURE — Esquema persistido en R2 (1:1 con WFSMaremotosGeocoder.parseFeature)
// =============================================================================

/**
 * Feature G12_38 almacenada en R2.
 *
 * El layout combina cuatro grupos de campos (HX-CAPTURA-MAXIMAL pleno):
 *
 * 1. **Atributos fuente íntegros** (origen WFS DERA G12, schema empírico
 *    `DescribeFeatureType` 2026-05-18): `id_dera`, `localidad`, `cod_mun`,
 *    `municipio`, `provincia`.
 * 2. **Campos derivados de geometría** (preservan fidelidad al schema fuente):
 *    `geom_type`, `geom_pointCount`.
 * 3. **Campos semánticos PTEL** (taxonomía cerrada TUN1 + decreto):
 *    `tipologiaPTEL`, `tipoOriginal`, `decretoReferencia`, `contextoEmergencia`.
 * 4. **Campos provenance** (re-validación sin re-consultar metadata):
 *    `source_layer`, `source_endpoint`, `schema_version`, `fetched_at`.
 *
 * Además incluye coordenadas normalizadas `x`, `y` (EPSG:25830) y un
 * `name` canónico para el motor de fuzzy-matching (localidad o municipio).
 */
export interface DeraG12_38Feature {
  // ---- Identificación y coordenadas normalizadas ------------------------
  /** ID DERA original (numérico en fuente, string en R2 por compatibilidad). */
  id_dera: string;
  /** Nombre canónico para fuzzy-matching: `localidad` → `municipio` → fallback. */
  name: string;
  /** Coordenada X UTM30 EPSG:25830. */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830. */
  y: number;

  // ---- Atributos fuente íntegros (HX-CAPTURA-MAXIMAL §1) ----------------
  /** Núcleo poblacional; 77 % de los features (NULL-permisivo en fuente). */
  localidad: string | null;
  /** Código INE municipio (5 dígitos). */
  cod_mun: string;
  /** Nombre municipio. */
  municipio: string;
  /** Provincia. */
  provincia: string;

  // ---- Campos derivados de geometría (HX-CAPTURA-MAXIMAL §2) ------------
  /** Tipo geométrico GeoJSON original (sample 100 % `MultiPoint`). */
  geom_type: string;
  /** Cardinalidad MultiPoint (sample 100 % = 1). */
  geom_pointCount: number;

  // ---- Campos semánticos PTEL (HX-CAPTURA-MAXIMAL §3) --------------------
  /** Tipología macro TUN1 cerrada (siempre `'EMERGENCIAS'` para esta capa). */
  tipologiaPTEL: 'EMERGENCIAS';
  /** Tipo granular específico (no contamina la taxonomía cerrada). */
  tipoOriginal: 'PUNTO_ENCUENTRO_MAREMOTO';
  /** Decreto de referencia institucional. */
  decretoReferencia: '197/2024';
  /** Contexto operativo de emergencia. */
  contextoEmergencia: 'EVACUACION_MAREMOTO_LITORAL';

  // ---- Campos provenance (HX-CAPTURA-MAXIMAL §4) ------------------------
  /** Identificador completo de la capa fuente. */
  source_layer: string;
  /** Endpoint WFS de origen. */
  source_endpoint: string;
  /** Versión del schema empírico — coordinado con SCHEMA_VERSION_G12_38. */
  schema_version: string;
  /** Timestamp de captura ISO-8601 UTC. */
  fetched_at: string;
}

// =============================================================================
// OPCIONES Y RESULTADOS
// =============================================================================

/** Opciones para búsqueda en G12_38 R2. */
export interface DeraG12_38SearchOptions {
  /** Código INE municipio (5 dígitos) — REQUERIDO. */
  codINE: string;
  /** Nombre del punto/localidad para fuzzy match (opcional). */
  name?: string;
  /** Umbral mínimo confianza (0-100, default: 60). */
  minConfidence?: number;
  /** Máximo resultados (default: 5). */
  maxResults?: number;
}

/** Resultado de búsqueda G12_38 R2. */
export interface DeraG12_38Result {
  /** Coordenada X UTM30 EPSG:25830. */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830. */
  y: number;
  /** Confianza (0-100). */
  confidence: number;
  /** Precisión estimada metros (DERA oficial). */
  precision: number;
  /** Identificador de fuente. */
  source: 'dera_g12_38_r2';
  /** Feature encontrada. */
  feature: DeraG12_38Feature;
  /** Score fuzzy (0-1) si aplica. */
  fuzzyScore?: number;
}

// =============================================================================
// CONSTANTES VALIDACIÓN
// =============================================================================

/** Rango válido X UTM30 Andalucía (skill ptel-geocoding). */
export const G12_38_X_RANGE = { min: 100_000, max: 900_000 };
/** Rango válido Y UTM30 Andalucía (skill ptel-geocoding). */
export const G12_38_Y_RANGE = { min: 3_900_000, max: 4_400_000 };
/** Precisión estimada DERA G12 (metros) — coordenadas oficiales EPSG:25830 nativas. */
export const G12_38_PRECISION_M = 10;

/**
 * Versión del schema empírico — debe mantenerse alineada con
 * `WFSMaremotosGeocoder.SCHEMA_VERSION_G12_38`. Incrementar si el
 * `DescribeFeatureType` upstream cambia (nuevos campos, nuevas
 * cardinalidades, cambio de tipo).
 */
export const R2_SCHEMA_VERSION_G12_38 = 'g12_38_v20260518' as const;

/** Identificador completo de la capa fuente WFS. */
export const G12_38_SOURCE_LAYER =
  'DERA_g12_servicios:g12_38_PuntosEncuentroMaremotos' as const;

/** Endpoint WFS DERA G12. */
export const G12_38_SOURCE_ENDPOINT =
  'https://www.ideandalucia.es/services/DERA_g12_servicios/wfs' as const;
